/**
 * <PortalSandboxPage /> — shared 5-section template per §4.2.
 *
 * Sections:
 *   1. Header with SandboxModeBadge + bilingual portal name
 *   2. "Configure production credentials" CTA — disabled until accredited
 *   3. Mutaba'aa veto test workbench (per-portal test cards)
 *   4. Article-specific deadline trackers (accreditation-pending placeholders)
 *   5. Recent API calls table (audit-log feed)
 */

import { useQuery } from "@tanstack/react-query";
import { Lock, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { api } from "../../lib/api";
import type { GovConfigRow } from "../../lib/api";
import { PageHeader } from "../ui/PageHeader";
import { HealthCard } from "./HealthCard";
import { MutabaaVetoTestForm } from "./MutabaaVetoTestForm";
import type { PortalConfig } from "./portalConfigs";
import { RecentCallsTable } from "./RecentCallsTable";
import { SandboxModeBadge } from "./SandboxModeBadge";

// v1 LE resolution — same placeholder used in GovIntegrationsHub.
// Real Clerk-org LE resolution lands per the platform-wide follow-up.
const _DEMO_LE_ID = "00000000-0000-0000-0000-000000000001";

export interface PortalSandboxPageProps {
  config: PortalConfig;
}

export function PortalSandboxPage({ config }: PortalSandboxPageProps) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const legalEntityId = _DEMO_LE_ID;

  const configQ = useQuery({
    queryKey: ["gov-config", legalEntityId],
    queryFn: () => api.listGovConfigs(legalEntityId),
  });

  const portalCfg = configQ.data?.find(
    (c: GovConfigRow) => c.portal === config.portal,
  );
  const mode = portalCfg?.mode ?? "sandbox";
  const accredited = portalCfg?.production_unlocked_at != null;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t(config.titleKey, config.titleFallback)}
        subtitle={t(config.subtitleKey, config.subtitleFallback)}
        breadcrumbs={[
          { label: t("platform.breadcrumb_settings", "Settings"), href: "/settings" },
          {
            label: t("platform.breadcrumb_integrations", "Integrations"),
            href: "/settings/integrations",
          },
          {
            label: t("gov.hub_title", "Government Integrations"),
            href: "/settings/integrations/gov",
          },
          { label: t(config.titleKey, config.titleFallback) },
        ]}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p
          className="text-xs text-text-tertiary"
          dir={isAr ? "rtl" : "ltr"}
        >
          {t(config.arTitleKey, config.arTitleFallback)}
        </p>
        <SandboxModeBadge mode={mode} />
      </div>

      {/* ---- Section 2: production credentials CTA -------------- */}
      <section
        data-testid={`production-creds-cta-${config.portal}`}
        className="rounded-xl border border-border-subtle bg-surface-default p-4"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-text-primary">
              {t(
                "gov.configure_production_credentials_cta",
                "Configure production credentials",
              )}
            </h2>
            <p className="mt-1 text-xs text-text-tertiary">
              {accredited
                ? t(
                    "gov.configure_production_credentials_available",
                    "Production credentials can be provisioned now.",
                  )
                : t(
                    "gov.configure_production_credentials_disabled",
                    "Available once Solvait accreditation completes for this Legal Entity",
                  )}
            </p>
          </div>
          <button
            type="button"
            disabled={!accredited}
            className="inline-flex shrink-0 items-center gap-1 rounded-sm border border-border bg-surface-default px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Lock size={11} aria-hidden />
            {accredited
              ? t("gov.configure_now", "Configure")
              : t("gov.configure_locked", "Locked")}
          </button>
        </div>
      </section>

      {/* ---- Section 3: workbench -------------------------------- */}
      <section data-testid={`workbench-${config.portal}`}>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-text-tertiary">
          {t("gov.workbench_title", "Test the Mutaba'aa veto")}
        </h2>
        <p className="mt-1 text-xs text-text-tertiary">
          {t(
            "gov.workbench_subtitle",
            "Run sandbox tests with deliberately-bad inputs to demonstrate the statutory floor enforcement. No real government API call is made.",
          )}
        </p>
        <div className="mt-3 flex flex-col gap-3">
          {config.workbenchTests.map((test) => (
            <MutabaaVetoTestForm
              key={test.id}
              portal={config.portal}
              legalEntityId={legalEntityId}
              test={test}
            />
          ))}
        </div>
      </section>

      {/* ---- Section 4: deadline trackers ----------------------- */}
      <section data-testid={`deadline-trackers-${config.portal}`}>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-text-tertiary">
          {t(
            "gov.deadline_trackers_title",
            "Article-specific deadline trackers",
          )}
        </h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {config.deadlineTrackers.map((tracker) => (
            <article
              key={tracker.id}
              className="flex flex-col gap-2 rounded-xl border border-border-subtle bg-surface-sunken p-3"
            >
              <header className="flex items-start justify-between gap-2">
                <h3 className="text-xs font-medium text-text-primary">
                  {t(tracker.titleKey, tracker.titleFallback)}
                </h3>
                <span className="shrink-0 rounded-full bg-brand-blue/10 px-2 py-0.5 text-[9px] uppercase text-brand-deep">
                  {tracker.article}
                </span>
              </header>
              <p className="text-[11px] text-text-tertiary leading-relaxed">
                {t(tracker.descKey, tracker.descFallback)}
              </p>
              {!accredited && (
                <span className="inline-flex w-fit items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] uppercase text-amber-800 ring-1 ring-amber-200">
                  <Sparkles size={9} aria-hidden />
                  {t(
                    "gov.deadline_tracker_activates_with_credentials",
                    "Activates with production credentials",
                  )}
                </span>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* ---- Section 5: recent API calls ------------------------- */}
      <section className="rounded-xl border border-border-subtle bg-surface-default p-4">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-text-tertiary">
          {t("gov.recent_calls_title", "Recent API calls")}
        </h2>
        <RecentCallsTable
          portal={config.portal}
          legalEntityId={legalEntityId}
        />
      </section>
    </div>
  );
}

export default PortalSandboxPage;
// Re-export for landing page consumption
export { HealthCard };
